import React from "react";
import { translate, getLanguage } from "react-switch-lang";

const privacyUseItems = [
  "Provide, operate, and maintain our website",
  "Improve, personalize, and expand our website",
  "Understand and analyze how you use our website",
  "Develop new products, services, features, and functionality",
  "Communicate with you, either directly or through one of our partners, including for customer service, updates, and marketing purposes",
  "Send you emails",
  "Find and prevent fraud",
];

class Help extends React.Component {
  renderPolicySection(title, paragraphs, children = null) {
    return (
      <section className="help-page__policy-block" key={title}>
        <h3>{title}</h3>
        {paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        {children}
      </section>
    );
  }

  renderEnglishPage() {
    return (
      <div className="help-page help-page--en">
        <div className="help-page__shell">
          <section className="help-page__hero">
            <p className="help-page__eyebrow">Guide</p>
            <h1>How to use The Resource Map</h1>
            <p className="help-page__lede">
              Browse the project goals, watch the walkthrough, and review the
              privacy policy in a cleaner left-aligned layout.
            </p>
          </section>
          <div className="help-page__content-grid">
            <section className="help-page__intro help-page__panel">
              <h2>Introduction</h2>
              <p>
                Government agencies and NGOs have been working hard to serve the
                low-income populations and street sleepers in Hong Kong, but to
                date, there is no comprehensive resource map specifically for
                these groups to use in Hong Kong.
              </p>
              <p>
                The Social Welfare Department maintains a geoinfo map covering
                general social services, but it does not plot out in-kind
                contributions such as meals and medical vouchers. The purpose of
                this app is to close that gap and help maximize access to
                available resources.
              </p>
              <p>
                We hope that this website provides NGOs, social workers,
                volunteers, and direct end-users with an easier way to identify
                resources and better serve the needy.
              </p>
              <p className="help-page__source-note">
                Service Map data comes from the{" "}
                <a
                  className="help-page__source-link"
                  href="https://www.swd.gov.hk/en/svcdesk/download/listofserv/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Social Welfare Department list of services
                </a>
                , updated April 2026. Resource Map sources are updated
                separately.
              </p>
            </section>
            <section className="help-page__video-panel help-page__panel">
              <div className="help-page__section-heading">
                <p className="help-page__eyebrow">Walkthrough</p>
                <h2>See the map in action</h2>
              </div>
              <p className="help-page__supporting-copy">
                Watch the English demo to learn how to browse the map, switch
                views, and inspect service details.
              </p>
              <div className="help-page__video-shell">
                <iframe
                  src="https://www.youtube.com/embed/b5nNL-smjcg"
                  title="Resource Map walkthrough"
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="help-page__video"
                />
              </div>
            </section>
          </div>
          <section className="help-page__policy help-page__panel">
            <div className="help-page__section-heading">
              <p className="help-page__eyebrow">Policy</p>
              <h2>Privacy Policy for The Resource Map</h2>
            </div>
            <div className="help-page__policy-copy">
              <p>
                At The Resource Map, accessible from www.theresourcemap.com, one
                of our main priorities is the privacy of our visitors. This
                privacy policy explains the information that is collected and how
                it is used.
              </p>
              <p>
                If you have additional questions or need more information about
                our privacy policy, please contact us.
              </p>
              {this.renderPolicySection("Consent", [
                "By using our website, you hereby consent to our Privacy Policy and agree to its terms.",
              ])}
              {this.renderPolicySection("Information we collect", [
                "The personal information that you are asked to provide, and the reasons why you are asked to provide it, will be made clear to you when requested.",
                "If you contact us directly, we may receive additional information such as your name, email address, phone number, the contents of your message, and any attachments you choose to send.",
              ])}
              {this.renderPolicySection(
                "How we use your information",
                [
                  "We use the information we collect in various ways, including to:",
                ],
                <ul className="help-page__list">
                  {privacyUseItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              {this.renderPolicySection("Children's Information", [
                "Another part of our priority is adding protection for children while using the internet. We encourage parents and guardians to observe, participate in, and monitor their online activity.",
                "The Resource Map does not knowingly collect any personally identifiable information from children under the age of 13. If you think your child provided this kind of information on our website, please contact us and we will do our best to remove it promptly.",
              ])}
            </div>
          </section>
        </div>
      </div>
    );
  }

  renderChinesePage() {
    return (
      <div className="help-page help-page--cn">
        <div className="help-page__shell">
          <section className="help-page__hero">
            <p className="help-page__eyebrow">指南</p>
            <h1>如何使用資訊地圖</h1>
            <p className="help-page__lede">
              以更清楚的左對齊版面快速了解平台目標，並觀看中文示範影片。
            </p>
          </section>
          <div className="help-page__content-grid">
            <section className="help-page__intro help-page__panel">
              <h2>引言</h2>
              <p>
                香港的政府機構和非政府組織一直在努力為露宿者和低收入人士提供支援服務，但至今仍未有一個資訊地圖能夠全面而清楚地列出相關資源。
              </p>
              <p>
                社會福利署的地理資訊地圖涵蓋一般社會服務，但沒有完整整理實物援助等資訊。因此，我們開發了這個平台，希望為非政府組織、社工、義工和受助者提供更直接的資源入口。
              </p>
              <p className="help-page__source-note">
                服務地圖資料來自
                <a
                  className="help-page__source-link"
                  href="https://www.swd.gov.hk/en/svcdesk/download/listofserv/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  社會福利署服務名單
                </a>
                ，更新至 2026 年 4 月。資源地圖資料來源則另行更新。
              </p>
            </section>
            <section className="help-page__video-panel help-page__panel">
              <div className="help-page__section-heading">
                <p className="help-page__eyebrow">教學影片</p>
                <h2>觀看操作示範</h2>
              </div>
              <p className="help-page__supporting-copy">
                影片會示範如何瀏覽地圖、切換內容，以及查看不同資源詳情。
              </p>
              <div className="help-page__video-shell">
                <iframe
                  src="https://www.youtube.com/embed/EosP6pgtJV0?si=BniQiym2YscRrPyy"
                  title="Resource Map walkthrough"
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="help-page__video"
                />
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return getLanguage() === "en"
      ? this.renderEnglishPage()
      : this.renderChinesePage();
  }
}

export default translate(Help);
